/**
 * Updated property detail view (opened from a Properties card). Mirrors
 * `design/Syntra/property-detail.html`: a two-column header (property card +
 * Rehab summary card) and the "Property Cash Flow" panel.
 *
 * Per the brief, the "Rehab" and "Full underwriting" top-right pill buttons are
 * intentionally NOT rendered, and future months are not projected.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  CheckCircle2,
  Home,
  Pencil,
  Repeat,
  Tag,
  Wrench,
} from "lucide-react";
import { format, parseISO } from "date-fns";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  closingCost,
  computeFlip,
  fmtMoney,
  interestOnlyMonthly,
  loanAmount,
  originationFee,
} from "@/lib/calculators";
import { resolveSellingCost } from "@/lib/property-setting";
import {
  buildYearMonths,
  deriveRehabBudget,
  effectiveRehabEnd,
  normalizeFromSettings,
  rehabItemsByCompletedMonth,
  rehabSpendByCompletedMonth,
} from "@/components/properties/cashflow-metrics";
import {
  overlayRentPayments,
  overlayYearExpenses,
  summarizeYear,
  type YearMonthWithLogged,
} from "@/components/properties/cashflow-detail";
import { AddExpenseDialog } from "@/components/properties/detail/AddExpenseDialog";
import { CashflowBreakdownDrawer } from "@/components/properties/detail/CashflowBreakdownDrawer";
import { EditPriceDialog } from "@/components/properties/detail/EditPriceDialog";
import {
  MarkSoldDialog,
  type MarkSoldValues,
} from "@/components/properties/detail/MarkSoldDialog";
import { PropertyCashflowPanel } from "@/components/properties/detail/PropertyCashflowPanel";
import { PropertyFlipPanel } from "@/components/properties/detail/PropertyFlipPanel";
import { RehabSummaryDrawer } from "@/components/properties/detail/RehabSummaryDrawer";
import { useDealSettings } from "@/hooks/use-deal-settings";
import { useMarkSold } from "@/hooks/use-mark-sold";
import type { OwnedProperty } from "@/hooks/use-owned-properties";
import { usePropertyExpenses } from "@/hooks/use-property-expenses";
import { usePropertyLogs } from "@/hooks/use-property-logs";
import { usePropertyRehab } from "@/hooks/use-property-rehab";
import { usePropertyRentPayments } from "@/hooks/use-property-rent-payments";
import { usePropertyRentalStatus } from "@/hooks/use-property-rental-status";

const STATUS_LABEL: Record<OwnedProperty["status"], string> = {
  in_rehab: "In Rehab",
  owned: "Owned",
  sold: "Sold",
};

const PROPERTY_TYPE_LABEL: Record<string, string> = {
  sfh: "Single Family",
  mfh: "Multi-Family",
  condo: "Condo",
  townhouse: "Townhouse",
  land: "Land",
  commercial: "Commercial",
};

const STRATEGY_ICON = {
  BRRRR: Repeat,
  Flip: ArrowLeftRight,
  Rental: Home,
} as const;

interface PropertyDetailViewProps {
  item: OwnedProperty;
  onBack: () => void;
}

export function PropertyDetailView({ item, onBack }: PropertyDetailViewProps) {
  const navigate = useNavigate();
  const { settings, saveSettings, rehabRows } = useDealSettings(item.id);
  const { logsByProperty } = usePropertyLogs();
  const { expenses, createExpense, deleteExpense } = usePropertyExpenses(
    item.id,
  );
  const { rehab } = usePropertyRehab(item.id);
  const { rentByMonth } = usePropertyRentPayments(item.id);
  const { isRented } = usePropertyRentalStatus(item.id);
  const markSold = useMarkSold(item.id);

  const [editOpen, setEditOpen] = useState(false);
  const [soldOpen, setSoldOpen] = useState(false);
  const [rehabSummaryOpen, setRehabSummaryOpen] = useState(false);
  const [expenseMonth, setExpenseMonth] = useState<YearMonthWithLogged | null>(
    null,
  );
  // Track the breakdown target by calendar key so the drawer stays in sync with
  // the recomputed months after an expense is added/removed.
  const [breakdownKey, setBreakdownKey] = useState<number | null>(null);
  const [year, setYear] = useState(() => new Date().getUTCFullYear());

  const strategy = item.strategy ?? "BRRRR";
  const isFlip = strategy === "Flip";
  const isSold = item.status === "sold";
  const purchase = Number(settings?.purchase_price) || item.purchasePrice || 0;
  const arv =
    Number(settings?.after_repair_value) || Math.round(purchase * 1.28);
  // Budget vs. spend is derived from the live scope rows, NOT the persisted
  // `rehab_projects` roll-up — see `deriveRehabBudget` for why the roll-up
  // leaves the progress bar frozen while a rehab is in progress.
  const rehabBudget = useMemo(
    () =>
      deriveRehabBudget(rehabRows, {
        projectEstimated: rehab?.estimated,
        projectActual: rehab?.actual,
        repairCostEstimate: Number(settings?.repair_cost) || 0,
      }),
    [rehabRows, rehab?.estimated, rehab?.actual, settings?.repair_cost],
  );
  const rehabActual = rehabBudget.actual;
  const rehabEstimate = rehabBudget.estimate;
  const allIn = purchase + rehabActual;
  const equity = arv - allIn;

  const logs = useMemo(
    () => logsByProperty.get(item.id) ?? [],
    [logsByProperty, item.id],
  );

  const years = useMemo(() => {
    const set = new Set<number>([new Date().getUTCFullYear()]);
    for (const l of logs) set.add(l.year);
    return [...set].sort((a, b) => b - a);
  }, [logs]);

  // Only a genuinely completed item (status "completed" AND a completion date)
  // contributes rehab spend to a month — a pending item, or one whose date was
  // cleared, must not show its value or push a month into the rehab phase.
  const completedRehabRows = useMemo(
    () =>
      rehabRows.filter(
        (r) => r.status === "completed" && r.completed_at != null,
      ),
    [rehabRows],
  );

  // Distribute rehab spend across months by each completed item's completion
  // date. Every row here has a date (filter above), so no movedAt fallback is
  // needed — dateless/pending items are excluded rather than lumped elsewhere.
  const rehabSpendByMonth = useMemo(
    () => rehabSpendByCompletedMonth(completedRehabRows, null),
    [completedRehabRows],
  );

  // Same distribution, kept itemized (task name + spend) so the breakdown
  // drawer can list rehab spend per line item instead of a single lump.
  const rehabItemsByMonth = useMemo(
    () => rehabItemsByCompletedMonth(completedRehabRows, null),
    [completedRehabRows],
  );

  // Rehab ends when the line items are actually finished — not when the project
  // is manually "marked complete". Falls back to movedAt when there are no line
  // items or any are still pending. Drives the rehab/operating phase split.
  const rehabEndDate = useMemo(
    () => effectiveRehabEnd(rehabRows, item.movedAt),
    [rehabRows, item.movedAt],
  );

  const months = useMemo<YearMonthWithLogged[]>(() => {
    const live = normalizeFromSettings({
      strategy,
      settings: settings ?? item.settings,
      loan: item.loan,
      rentAmount: null,
    });
    const built = buildYearMonths({
      year,
      strategy,
      logs,
      live,
      acquisitionDate: item.ownedDate,
      rehabEndDate,
      rehabActual,
      // Distribute by line-item completion when we have any; otherwise fall
      // back to the lump `rehabActual` in the completion month so a project
      // tracked without line items still shows its rehab cost.
      rehabSpendByMonth:
        rehabSpendByMonth.size > 0 ? rehabSpendByMonth : undefined,
      // Flip: carry-only timeline (no rental income / reserves fabricated).
      holdingOnly: isFlip,
      // Once sold, no cash flow past the sale month.
      saleDate: item.closedDate,
    });
    // A flip earns no rent, so skip the rent overlay; only logged carry applies.
    const withRent = isFlip ? built : overlayRentPayments(built, rentByMonth);
    return overlayYearExpenses(withRent, expenses);
  }, [
    year,
    strategy,
    isFlip,
    settings,
    item.settings,
    item.loan,
    item.ownedDate,
    item.closedDate,
    rehabEndDate,
    rehabActual,
    rehabSpendByMonth,
    logs,
    rentByMonth,
    expenses,
  ]);

  const summary = useMemo(() => summarizeYear(months), [months]);

  // Re-derive the breakdown month from the live months so deletes reflect instantly.
  const breakdownMonth =
    breakdownKey != null
      ? (months.find((m) => m.year * 12 + (m.month - 1) === breakdownKey) ??
        null)
      : null;
  // Rehab line items completed in the breakdown month (label + spend) so the
  // drawer lists them by name instead of one "Total rehab" figure.
  const breakdownRehabItems =
    breakdownKey != null ? (rehabItemsByMonth.get(breakdownKey) ?? []) : [];

  const pd = item.property;
  const addr2 = [pd.city, pd.state].filter(Boolean).join(", ");
  const specs = [
    pd.bedrooms ? `${pd.bedrooms} bd` : "",
    pd.bathrooms ? `${pd.bathrooms} ba` : "",
    pd.sqft ? `${pd.sqft.toLocaleString()} sqft` : "",
    pd.propertyType ? (PROPERTY_TYPE_LABEL[pd.propertyType] ?? "") : "",
  ].filter(Boolean);
  const StrategyIcon = STRATEGY_ICON[strategy] ?? Repeat;

  const defaultExpenseDate = expenseMonth
    ? `${expenseMonth.year}-${String(expenseMonth.month).padStart(2, "0")}-01`
    : new Date().toISOString().slice(0, 10);
  const progress = rehabBudget.progressPct;
  const overBudget = rehabBudget.overBudget;

  // Flip economics — mirrors the pipeline Flip Analysis (computeFlip), reading the
  // persisted flip holding/selling assumptions. Actual rehab spend drives all-in;
  // sale proceeds default to ARV until a stored sold price exists.
  const flipSummary = useMemo(() => {
    if (!isFlip) return null;
    const hml = settings?.flip?.hard_money ?? null;
    const closingDollar = closingCost(
      purchase,
      Number(settings?.closing_pct) || 3,
    );
    const loanAmt = hml ? loanAmount(purchase, Number(hml.ltv) || 0) : 0;
    const origFeeAmt = hml
      ? originationFee(loanAmt, Number(hml.points) || 0)
      : 0;
    // Hard money is interest-only with a balloon: the monthly carry is interest
    // alone, and the principal leaves the deal once as `loanPayoff` at close.
    // Amortizing it here would double-charge principal and make `holding`
    // (and therefore profit / ROI / cash invested) an order of magnitude off.
    const monthlyDebt = hml
      ? interestOnlyMonthly(loanAmt, Number(hml.rate) || 0)
      : 0;
    const taxesMonthly =
      settings?.flip?.taxes_monthly ?? Math.round(purchase * 0.0018);
    const utilitiesMonthly = settings?.flip?.utilities_monthly ?? 100;
    // Once sold, actual sell-side dollars (closing_costs) drive selling — convert
    // to a percent of ARV (= sale price) so computeFlip reproduces the actuals.
    // Until then it's the estimate: the entered dollar amount, or the percent
    // of ARV for rows that predate it (`resolveSellingCost`).
    const sellPct =
      isSold && item.closingCosts != null && arv > 0
        ? (item.closingCosts / arv) * 100
        : resolveSellingCost(settings?.flip, arv).pct;
    const result = computeFlip({
      purchase,
      rehab: rehabActual || rehabEstimate,
      arv,
      closingCost: closingDollar,
      hmlLoanAmt: loanAmt,
      hmlOrigFee: origFeeAmt,
      hmlMonthlyDebt: monthlyDebt,
      monthlyTaxes: taxesMonthly,
      monthlyUtilities: utilitiesMonthly,
      holdMonths: settings?.flip?.hold_months ?? 6,
      sellPct,
    });
    return { result, loanPayoff: loanAmt };
  }, [
    isFlip,
    isSold,
    item.closingCosts,
    settings,
    purchase,
    arv,
    rehabActual,
    rehabEstimate,
  ]);

  // Inputs for the Mark-as-sold dialog's realized-profit preview.
  const soldAllInBeforeSelling = flipSummary
    ? flipSummary.result.allIn - flipSummary.result.selling
    : purchase +
      rehabActual +
      closingCost(purchase, Number(settings?.closing_pct) || 3);
  const soldLoanPayoff = flipSummary?.loanPayoff ?? 0;
  const soldDefaultSellPct = resolveSellingCost(settings?.flip, arv).pct;

  function handleConfirmSold(values: MarkSoldValues) {
    markSold.mutate(values, { onSuccess: () => setSoldOpen(false) });
  }

  return (
    <div className="space-y-5">
      <Button variant="ghost" size="sm" className="w-fit" onClick={onBack}>
        <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Properties
      </Button>

      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        {/* Property card */}
        <Card className="overflow-hidden">
          <div className="flex flex-col gap-4 md:flex-row">
            <div
              className="relative h-44 w-full bg-muted bg-cover bg-center md:h-auto md:w-60"
              style={
                pd.imageUrl
                  ? { backgroundImage: `url(${pd.imageUrl})` }
                  : undefined
              }
            >
              <Badge className="absolute left-3 top-3" variant="secondary">
                {STATUS_LABEL[item.status]}
              </Badge>
            </div>
            <div className="flex-1 space-y-3 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h1 className="text-xl font-bold tracking-tight text-foreground">
                    {pd.address ?? "Property"}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {addr2 || "—"}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className="gap-1 border-primary/30 bg-primary/10 text-primary"
                >
                  <StrategyIcon className="h-3 w-3" />
                  {strategy}
                </Badge>
              </div>
              {specs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {specs.map((s) => (
                    <span
                      key={s}
                      className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-end gap-8">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Purchase price
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {fmtMoney(purchase)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    All-in {fmtMoney(allIn)}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {isSold ? "Sold price" : "After-repair value"}
                  </div>
                  <div className="text-2xl font-bold text-success">
                    {fmtMoney(arv)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {isSold ? "Realized gain" : "Est. equity"}{" "}
                    {fmtMoney(equity)}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditOpen(true)}
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                  </Button>
                  {isSold ? (
                    <Badge className="gap-1 border-success/30 bg-success/10 text-success">
                      <Tag className="h-3 w-3" />
                      Sold
                      {item.closedDate &&
                        ` · ${format(parseISO(item.closedDate), "MMM d, yyyy")}`}
                    </Badge>
                  ) : (
                    // "Mark as sold" is a Flip-only action (disposition at resale).
                    isFlip && (
                      <Button size="sm" onClick={() => setSoldOpen(true)}>
                        <Tag className="mr-1.5 h-3.5 w-3.5" /> Mark as sold
                      </Button>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Rehab card */}
        <Card className="space-y-3 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Wrench className="h-4 w-4 text-warning" /> Rehab
            </div>
            {rehab?.isComplete && (
              <Badge
                variant="outline"
                className="gap-1 border-success/30 bg-success/10 text-[10px] font-semibold uppercase tracking-wide text-success"
              >
                <CheckCircle2 className="h-3 w-3" />
                Complete
              </Badge>
            )}
          </div>
          <div className="flex gap-8">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Estimated
              </div>
              <div className="text-xl font-bold text-muted-foreground">
                {fmtMoney(rehabEstimate)}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Actual spend
              </div>
              <div className="text-xl font-bold text-success">
                {fmtMoney(rehabActual)}
              </div>
            </div>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                "h-full",
                overBudget ? "bg-destructive" : "bg-success",
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Spent</span>
            <span>Budget {fmtMoney(rehabEstimate)}</span>
          </div>
          <div
            className={cn(
              "flex items-center gap-1.5 text-xs font-semibold",
              overBudget ? "text-destructive" : "text-success",
            )}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {overBudget
              ? `${fmtMoney(rehabActual - rehabEstimate)} over budget`
              : "On budget"}
          </div>
          <button
            type="button"
            onClick={() => setRehabSummaryOpen(true)}
            className="flex items-center gap-1 text-xs font-semibold text-primary transition-colors hover:text-primary/80"
          >
            View rehab summary <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </Card>
      </div>

      {isFlip && flipSummary ? (
        <PropertyFlipPanel
          year={year}
          years={years}
          onYearChange={setYear}
          months={months}
          flip={flipSummary.result}
          arv={arv}
          isSold={item.status === "sold"}
          loanPayoff={flipSummary.loanPayoff}
          onAddExpense={(m) => setExpenseMonth(m)}
          onViewBreakdown={(m) => setBreakdownKey(m.year * 12 + (m.month - 1))}
        />
      ) : (
        <PropertyCashflowPanel
          year={year}
          years={years}
          onYearChange={setYear}
          months={months}
          summary={summary}
          isRented={isRented}
          onAddExpense={(m) => setExpenseMonth(m)}
          onViewBreakdown={(m) => setBreakdownKey(m.year * 12 + (m.month - 1))}
        />
      )}

      <EditPriceDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        purchase={purchase}
        arv={arv}
        isSaving={saveSettings.isPending}
        onSubmit={(values) =>
          saveSettings.mutate(values, { onSuccess: () => setEditOpen(false) })
        }
      />

      <AddExpenseDialog
        open={expenseMonth != null}
        onOpenChange={(open) => !open && setExpenseMonth(null)}
        defaultDate={defaultExpenseDate}
        monthLabel={
          expenseMonth ? `${expenseMonth.monthName} ${expenseMonth.year}` : ""
        }
        isSaving={createExpense.isPending}
        onSubmit={(values) =>
          createExpense.mutate(values, {
            onSuccess: () => setExpenseMonth(null),
          })
        }
      />

      <CashflowBreakdownDrawer
        open={breakdownKey != null}
        onOpenChange={(open) => !open && setBreakdownKey(null)}
        month={breakdownMonth}
        rehabItems={breakdownRehabItems}
        strategy={strategy}
        isDeleting={deleteExpense.isPending}
        onDeleteExpense={(id) => deleteExpense.mutate(id)}
      />

      <RehabSummaryDrawer
        open={rehabSummaryOpen}
        onOpenChange={setRehabSummaryOpen}
        address={pd.address ?? "this property"}
        estimated={rehabEstimate}
        actual={rehabActual}
        isComplete={rehab?.isComplete ?? false}
        startDate={item.ownedDate}
        completedDate={rehab?.completedAt ?? rehabEndDate}
        items={rehabRows}
        onOpenRehabPage={() => navigate("/rehab")}
      />

      <MarkSoldDialog
        open={soldOpen}
        onOpenChange={setSoldOpen}
        address={pd.address ?? "this property"}
        arv={arv}
        allInBeforeSelling={soldAllInBeforeSelling}
        loanPayoff={soldLoanPayoff}
        defaultSellPct={soldDefaultSellPct}
        isSaving={markSold.isPending}
        onConfirm={handleConfirmSold}
      />
    </div>
  );
}
